import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Skeleton } from 'antd';
import { useDataProvider, useTranslate } from 'react-admin';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import keyBy from 'lodash/keyBy';
import { useSelector } from 'react-redux';

const { Option } = Select;

const AntdReferenceSelectWithSearch = ({
  reference,
  optionText = 'name',
  perPage = 25,
  filter = {},
  optionValue = 'id',
  value,
  onChange,
  ...rest
}) => {
  const isFirstRun = useRef(true);
  const t = useTranslate();
  const cachedOptions = useSelector(({ admin }) => admin.resources[reference]?.data || {});
  const dataProvider = useDataProvider();
  const [options, setOptions] = useState({});
  const [searchedOptions, setSearchedOptions] = useState();
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState('');

  const shouldGetInitOption = useMemo(() => {
    let result = !!value && !search;
    const data = {
      ...cachedOptions,
      ...options,
    };
    if (result) {
      if (Array.isArray(value)) {
        result = value.some((item) => !item[optionText] && !data[item[optionValue]]);
      } else {
        result = !value[optionText] && !data[value[optionValue]];
      }
    }
    return result;
  }, [options, value, search, optionValue, optionText, cachedOptions]);

  const fetchFn = useCallback(
    async (search, customFilter) => {
      setFetching(true);
      try {
        const commonFilter = {
          ...filter,
          ...customFilter,
        };
        const { data } = await dataProvider.getList(reference, {
          pagination: {
            page: 1,
            perPage,
          },
          sort: {
            field: 'id',
            order: 'ASC',
          },
          filter: search
            ? {
                'name||$cont': search,
                ...commonFilter,
              }
            : commonFilter,
        });

        const options = keyBy(data, 'id');

        if (search) {
          setSearchedOptions(options);
        } else {
          setSearchedOptions();
          setOptions(options);
        }
      } finally {
        setFetching(false);
      }
    },
    [reference, filter, perPage],
  );

  // Debounced fetch function
  const fetchOptions = useCallback(
    debounce(async (searchText) => {
      fetchFn(searchText);
      setSearch(searchText);
    }, 300),
    [dataProvider, fetchFn],
  );

  useEffect(() => {
    if (shouldGetInitOption && isFirstRun.current && reference) {
      fetchFn(
        '',
        Array.isArray(value)
          ? {
              'id||$in': value.map((item) => item[optionValue]),
            }
          : {
              'id||$eq': value[optionValue],
            },
      );
      isFirstRun.current = false;
    }
  }, [shouldGetInitOption]);

  useEffect(() => {
    if (search) {
      fetchOptions(search);
    }
  }, [search, fetchOptions]);

  const usedOptions = useMemo(() => (search ? searchedOptions : options) || {}, [search, searchedOptions, options]);

  return (
    <Select
      showSearch
      onChange={(value) => {
        if (Array.isArray(value)) {
          onChange?.(value.map((item) => usedOptions[item]));
        } else {
          onChange?.(usedOptions[value]);
        }
      }}
      onSearch={(search) => {
        setFetching(true);
        setSearch(search);
      }}
      notFoundContent={
        fetching ? (
          <Skeleton
            active
            round
            title={false}
            paragraph={{
              rows: 3,
            }}
          />
        ) : (
          t('common.text.noOptions')
        )
      }
      filterOption={false}
      allowClear
      onClear={() => setSearch('')}
      onSelect={() => setSearch('')}
      onFocus={() => !Object.values(options).length && fetchFn()}
      loading={fetching}
      value={Array.isArray(value) ? value.map((item) => item[optionValue]) : value?.[optionValue]}
      placement="bottomLeft"
      labelRender={({ label, value: id }) =>
        label ||
        value?.[optionText] ||
        {
          ...cachedOptions,
          ...searchedOptions,
          ...options,
        }?.[id]
      }
      {...rest}
    >
      {fetching ? (
        <Skeleton
          active
          round
          title={false}
          paragraph={{
            rows: 3,
          }}
        />
      ) : (
        Object.values(usedOptions).map((record) => (
          <Option key={record[optionValue]} value={record[optionValue]}>
            {typeof optionText === 'function' ? optionText(record) : record[optionText]}
          </Option>
        ))
      )}
    </Select>
  );
};

AntdReferenceSelectWithSearch.propTypes = {
  reference: PropTypes.string.isRequired,
  optionText: PropTypes.string,
  perPage: PropTypes.number,
  filter: PropTypes.object,
  optionValue: PropTypes.string,
};

export default AntdReferenceSelectWithSearch;
